import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Box, TextField, Typography, Grid } from '@material-ui/core';
import AddBox from '@material-ui/icons/AddBox';
import useBenchmarkSearch from 'hooks/useBenchmarkSearch';
import { Message, ThemeButton } from 'components/shared';
import CheckboxButton from 'components/buttons/CheckboxButton';
import LoadingSpinner from 'components/LoadingSpinner';

const useStyles = makeStyles(theme => ({
  searchBar: {
    display: 'flex'
  },
  searchField: {
    flexGrow: '1',
    marginRight: '10px'
  },
  searchInput: {
    padding: '0 4px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      borderColor: theme.palette.accent.main,
      color: theme.palette.accent.main
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light
    }
  },
  searchProperty: {
    margin: '3px 0'
  },
  searchFieldHeader: {
    fontSize: '0.7rem',
    color: theme.palette.grey.dark,
    fontStyle: 'italic'
  },
  results: {
    ...theme.scrollStyles,
    marginTop: '10px',
    maxHeight: isAdvanced => (isAdvanced ? '140px' : '245px'),
    overflow: 'auto'
  },
  resultPlaceholder: {
    color: theme.palette.grey.dark,
    fontSize: '0.9rem',
    fontStyle: 'italic'
  },
  resultItem: {
    display: 'flex',
    color: theme.palette.grey.dark,
    padding: '5px 0',
    borderBottom: `1px solid ${theme.palette.grey.medium}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    '& p': {
      fontSize: '0.9rem'
    },
    '& svg': {
      cursor: 'pointer',
      color: theme.palette.secondary.main,
      margin: '0 3px'
    }
  }
}));

const BenchmarkSearch = ({ isAdvanced, onItemAdded }) => {
  const [controls, results, search, requestState] = useBenchmarkSearch();

  const classes = useStyles(isAdvanced);

  const onKeyPress = e => {
    if (e.key === 'Enter') {
      search();
    }
  };

  const renderAdvancedSearch = () => {
    return (
      <Box className={classes.advancedSearch}>
        <Grid container direction="row" alignItems="flex-end" justify="center">
          <Grid className={classes.searchProperty} item xs={4}>
            <Typography className={classes.searchFieldHeader}>Series Type Id</Typography>
            <TextField
              value={controls.seriesTypeId}
              onChange={controls.onSeriesTypeIdChange}
              InputProps={{ className: classes.searchInput }}
            />
          </Grid>
          <Grid className={classes.searchProperty} item xs={4}>
            <Typography className={classes.searchFieldHeader}>Series Type Detail Id</Typography>
            <TextField
              value={controls.seriesTypeDetailId}
              onChange={controls.onSeriesTypeDetailsIdChange}
              InputProps={{ className: classes.searchInput }}
            />
          </Grid>
          <Grid className={classes.searchProperty} item xs={4}>
            <Typography className={classes.searchFieldHeader}>Value Type Id</Typography>
            <TextField
              value={controls.valueTypeId}
              onChange={controls.onValueTypeIdChange}
              InputProps={{ className: classes.searchInput }}
            />
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="flex-end" justify="center">
          <Grid className={classes.searchProperty} item xs={4}>
            <Typography className={classes.searchFieldHeader}>Periodicity Id</Typography>
            <TextField
              value={controls.periodicityId}
              onChange={controls.onPeriodicityIdChange}
              InputProps={{ className: classes.searchInput }}
            />
          </Grid>
          <Grid className={classes.searchProperty} item xs={4}>
            <Typography className={classes.searchFieldHeader}>Parent Object Type Id</Typography>
            <TextField
              value={controls.parentObjectTypeId}
              onChange={controls.onParentObjectTypeIdChange}
              InputProps={{ className: classes.searchInput }}
            />
          </Grid>
          <Grid className={classes.searchProperty} item xs={4}>
            <CheckboxButton
              checked={controls.isActive}
              onChange={controls.onIsActiveToggle}
              hasTransparentBg
              title="Is Active"
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderResults = () => {
    if (requestState.isLoading) {
      return <LoadingSpinner />;
    }
    if (requestState.isError && requestState.error) {
      return <Message type="error" text={requestState.error} />;
    }
    if (!results.length) {
      return <Typography className={classes.resultPlaceholder}>No search results</Typography>;
    }

    return results.map(benchmark => (
      <Box key={benchmark.timeSerieId} className={classes.resultItem}>
        <Typography>{benchmark.name}</Typography>
        <AddBox onClick={() => onItemAdded(benchmark)} />
      </Box>
    ));
  };

  return (
    <Box>
      <Box className={classes.searchBar}>
        <TextField
          className={classes.searchField}
          value={controls.searchCriteria}
          onKeyPress={onKeyPress}
          onChange={controls.onSearchCriteriaChange}
          InputProps={{ className: classes.searchInput }}
        />
        <ThemeButton themeColor="accent" size="small" onClick={search}>
          Search
        </ThemeButton>
      </Box>
      {isAdvanced && renderAdvancedSearch()}
      <Box className={classes.results}>{renderResults()}</Box>
    </Box>
  );
};

BenchmarkSearch.propTypes = {
  isAdvanced: PropTypes.bool.isRequired,
  onItemAdded: PropTypes.func.isRequired
};

export default BenchmarkSearch;
